import { I18nManager } from '@/ui';
import Svg, { Circle, Path, Rect } from '@/ui/svg';

// Icons traced 1:1 from the Direction-A prototype sprite (fan-app.html <defs>).
// Stroke icons share the Feather-style frame (viewBox 24, round caps, 1.9 weight).

type IconProps = { size?: number; color?: string };

// Direction-relative icons (chevrons, send, logout arrows) must mirror under RTL
// so "forward" still points with the reading direction. Non-directional icons don't flip.
const RTL_MIRROR = I18nManager.isRTL ? { transform: [{ scaleX: -1 } as const] } : undefined;

function Stroke({
  size = 22,
  color = '#EFF5F1',
  directional,
  children,
}: IconProps & { directional?: boolean; children: React.ReactNode }) {
  return (
    <Svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={1.9}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={directional ? RTL_MIRROR : undefined}
    >
      {children}
    </Svg>
  );
}

export function MailIcon(p: IconProps) {
  return (
    <Stroke {...p}>
      <Rect x={2} y={4} width={20} height={16} rx={2.5} />
      <Path d="m3 6.5 9 6 9-6" />
    </Stroke>
  );
}

export function PhoneIcon(p: IconProps) {
  return (
    <Stroke {...p}>
      <Path d="M5 2h4l2 5-2.5 1.5a12 12 0 0 0 5 5L20 11l2 4v4a2 2 0 0 1-2 2A18 18 0 0 1 3 4a2 2 0 0 1 2-2Z" />
    </Stroke>
  );
}

export function FingerprintIcon(p: IconProps) {
  return (
    <Stroke {...p}>
      <Path d="M12 11a2 2 0 0 1 2 2c0 3-1 5-1 5M8 13a4 4 0 0 1 8 0c0 4-1 6-1 6M5.5 11a7 7 0 0 1 13 0c0 2 0 3-.5 5M12 2a9 9 0 0 0-8 5" />
    </Stroke>
  );
}

export function ChevronRightIcon(p: IconProps) {
  return (
    <Stroke {...p} directional>
      <Path d="M9 18l6-6-6-6" />
    </Stroke>
  );
}

export function ChevronLeftIcon(p: IconProps) {
  return (
    <Stroke {...p} directional>
      <Path d="M15 18l-6-6 6-6" />
    </Stroke>
  );
}

export function ShieldIcon(p: IconProps) {
  return (
    <Stroke {...p}>
      <Path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10Z" />
    </Stroke>
  );
}

export function ChevronDownIcon(p: IconProps) {
  return (
    <Stroke {...p}>
      <Path d="M6 9l6 6 6-6" />
    </Stroke>
  );
}

export function InfoIcon(p: IconProps) {
  return (
    <Stroke {...p}>
      <Path d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18M12 11v5M12 8h.01" />
    </Stroke>
  );
}

export function CheckIcon(p: IconProps) {
  return (
    <Stroke {...p}>
      <Path d="M20 6 9 17l-5-5" />
    </Stroke>
  );
}

export function UserCheckIcon(p: IconProps) {
  return (
    <Stroke {...p}>
      <Path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8M16 11l2 2 4-4" />
    </Stroke>
  );
}

export function MessageCircleIcon(p: IconProps) {
  return (
    <Stroke {...p}>
      <Path d="M21 11.5a8 8 0 0 1-11.5 7L3 21l2.5-6.5A8 8 0 1 1 21 11.5Z" />
    </Stroke>
  );
}

export function CreditCardIcon(p: IconProps) {
  return (
    <Stroke {...p}>
      <Path d="M2 5h20v14H2zM2 10h20" />
    </Stroke>
  );
}

export function CameraIcon(p: IconProps) {
  return (
    <Stroke {...p}>
      <Path d="M3 8a2 2 0 0 1 2-2h2l1.5-2h7L17 6h2a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />
      <Path d="M12 16.4a3.4 3.4 0 1 0 0-6.8 3.4 3.4 0 0 0 0 6.8Z" />
    </Stroke>
  );
}

export function BellIcon(p: IconProps) {
  return (
    <Stroke {...p}>
      <Path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 0 1-3.4 0" />
    </Stroke>
  );
}

export function FilterIcon(p: IconProps) {
  return (
    <Stroke {...p}>
      <Path d="M3 5h18l-7 8v6l-4-2v-4Z" />
    </Stroke>
  );
}

export function CartIcon({ size = 22, color = '#EFF5F1' }: IconProps) {
  return (
    <Svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={1.9}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <Path d="M2 3h3l2.6 13.4a1.5 1.5 0 0 0 1.5 1.2h8.5a1.5 1.5 0 0 0 1.5-1.2L23 7H6" />
      <Circle cx={9} cy={21} r={1.5} fill={color} stroke="none" />
      <Circle cx={18} cy={21} r={1.5} fill={color} stroke="none" />
    </Svg>
  );
}

export function ClockIcon(p: IconProps) {
  return (
    <Stroke {...p}>
      <Circle cx={12} cy={12} r={9} />
      <Path d="M12 7v5l3 2" />
    </Stroke>
  );
}

export function PinIcon(p: IconProps) {
  return (
    <Stroke {...p}>
      <Path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0Z" />
      <Circle cx={12} cy={10} r={3} />
    </Stroke>
  );
}

export function BallIcon(p: IconProps) {
  return (
    <Stroke {...p}>
      <Circle cx={12} cy={12} r={9.2} />
      <Path d="M3 12h18M12 2.8v18.4M5.5 5.5c3.6 3 9.4 3 13 0M5.5 18.5c3.6-3 9.4-3 13 0" />
    </Stroke>
  );
}

export function HomeIcon(p: IconProps) {
  return (
    <Stroke {...p}>
      <Path d="M3 10.5 12 3l9 7.5M5 9.5V21h14V9.5" />
    </Stroke>
  );
}

export function ActivityIcon(p: IconProps) {
  return (
    <Stroke {...p}>
      <Path d="M22 12h-4l-3 9L9 3l-3 9H2" />
    </Stroke>
  );
}

export function BagIcon(p: IconProps) {
  return (
    <Stroke {...p}>
      <Path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4ZM3 6h18M16 10a4 4 0 0 1-8 0" />
    </Stroke>
  );
}

export function UsersIcon(p: IconProps) {
  return (
    <Stroke {...p}>
      <Path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
    </Stroke>
  );
}

export function UserIcon(p: IconProps) {
  return (
    <Stroke {...p}>
      <Path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8" />
    </Stroke>
  );
}

const STAR_D = 'M12 2.5l2.9 6.3 6.6.6-5 4.3 1.5 6.5L12 17l-5.5 3.2L8 13.7l-5-4.3 6.6-.6Z';

/** Filled star — used for the gold points chip / mission reward. */
export function StarIcon({ size = 14, color = '#C9A84C' }: IconProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24">
      <Path d={STAR_D} fill={color} />
    </Svg>
  );
}

export function GiftIcon(p: IconProps) {
  return (
    <Stroke {...p}>
      <Path d="M20 12v9H4v-9M2 7h20v5H2zM12 22V7M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7ZM12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7Z" />
    </Stroke>
  );
}

export function TargetIcon({ size = 22, color = '#EFF5F1' }: IconProps) {
  return (
    <Stroke size={size} color={color}>
      <Circle cx={12} cy={12} r={9} />
      <Circle cx={12} cy={12} r={5} />
      <Circle cx={12} cy={12} r={1.4} fill={color} />
    </Stroke>
  );
}

export function CalendarIcon(p: IconProps) {
  return (
    <Stroke {...p}>
      <Rect x={3} y={5} width={18} height={16} rx={2.5} />
      <Path d="M3 10h18M8 3v4M16 3v4" />
    </Stroke>
  );
}

export function TicketIcon(p: IconProps) {
  return (
    <Stroke {...p}>
      <Path d="M3 8a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2 2 2 0 0 0 0 4 2 2 0 0 1-2 2H5a2 2 0 0 1-2-2 2 2 0 0 0 0-4Z" />
      <Path d="M14 6v12" />
    </Stroke>
  );
}

export function GearIcon(p: IconProps) {
  return (
    <Stroke {...p}>
      <Circle cx={12} cy={12} r={3} />
      <Path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
    </Stroke>
  );
}

export function HelpIcon({ size = 22, color = '#EFF5F1' }: IconProps) {
  return (
    <Stroke size={size} color={color}>
      <Circle cx={12} cy={12} r={9} />
      <Path d="M9.5 9a2.5 2.5 0 1 1 3.5 2.3c-.8.4-1 .9-1 1.7M12 17h.01" />
    </Stroke>
  );
}

export function ShareIcon({ size = 22, color = '#EFF5F1' }: IconProps) {
  return (
    <Stroke size={size} color={color}>
      <Circle cx={18} cy={5} r={3} />
      <Circle cx={6} cy={12} r={3} />
      <Circle cx={18} cy={19} r={3} />
      <Path d="m8.6 13.5 6.8 4M15.4 6.5l-6.8 4" />
    </Stroke>
  );
}

export function LockIcon(p: IconProps) {
  return (
    <Stroke {...p}>
      <Rect x={4} y={11} width={16} height={10} rx={2.5} />
      <Path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </Stroke>
  );
}

export function GlobeIcon({ size = 22, color = '#EFF5F1' }: IconProps) {
  return (
    <Stroke size={size} color={color}>
      <Circle cx={12} cy={12} r={9} />
      <Path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" />
    </Stroke>
  );
}

export function MoonIcon(p: IconProps) {
  return (
    <Stroke {...p}>
      <Path d="M21 12.8A8.5 8.5 0 1 1 11.2 3a6.5 6.5 0 0 0 9.8 9.8Z" />
    </Stroke>
  );
}

export function LogoutIcon(p: IconProps) {
  return (
    <Stroke {...p} directional>
      <Path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
    </Stroke>
  );
}

export function TrashIcon(p: IconProps) {
  return (
    <Stroke {...p}>
      <Path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6M10 11v5M14 11v5" />
    </Stroke>
  );
}

export function WalletIcon(p: IconProps) {
  return (
    <Stroke {...p}>
      <Rect x={1} y={4} width={22} height={16} rx={2.5} />
      <Path d="M1 10h22M19 16a2 2 0 1 1 0-4 2 2 0 0 1 0 4Z" />
    </Stroke>
  );
}

export function SettingsIcon(p: IconProps) {
  return (
    <Stroke {...p}>
      <Circle cx={12} cy={12} r={3} />
      <Path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
    </Stroke>
  );
}

export function MoreIcon(p: IconProps) {
  return (
    <Stroke {...p}>
      <Circle cx={12} cy={5} r={1} fill={p.color} stroke="none" />
      <Circle cx={12} cy={12} r={1} fill={p.color} stroke="none" />
      <Circle cx={12} cy={19} r={1} fill={p.color} stroke="none" />
    </Stroke>
  );
}

export function BoltIcon(p: IconProps) {
  return (
    <Stroke {...p}>
      <Path d="M13 2 4 14h7l-1 8 9-12h-7l1-8Z" />
    </Stroke>
  );
}

export function XIcon(p: IconProps) {
  return (
    <Stroke {...p}>
      <Path d="M18 6 6 18M6 6l12 12" />
    </Stroke>
  );
}

export function PlusIcon(p: IconProps) {
  return (
    <Stroke {...p}>
      <Path d="M12 5v14M5 12h14" />
    </Stroke>
  );
}

export function MinusIcon(p: IconProps) {
  return (
    <Stroke {...p}>
      <Path d="M5 12h14" />
    </Stroke>
  );
}

const MEDAL_D =
  'M12 15a5 5 0 1 0 0-10 5 5 0 0 0 0 10Z M8.2 13.3 5 21l7-3 7 3-3.2-7.7';

/** Filled medal — podium rank marker for the prediction leaderboard. */
export function MedalIcon({ size = 20, color = '#C9A84C' }: IconProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24">
      <Path d={MEDAL_D} fill={color} />
    </Svg>
  );
}

export function SearchIcon(p: IconProps) {
  return (
    <Stroke {...p}>
      <Path d="M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14ZM20 20l-3.5-3.5" />
    </Stroke>
  );
}

export function SendIcon(p: IconProps) {
  return (
    <Stroke {...p} directional>
      <Path d="M22 2 11 13M22 2l-7 20-4-9-9-4 20-7Z" />
    </Stroke>
  );
}

const HEART_D = 'M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 1 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78Z';

export function HeartIcon(p: IconProps) {
  return (
    <Stroke {...p}>
      <Path d={HEART_D} />
    </Stroke>
  );
}

export function HeartFilledIcon({ size = 22, color = '#E8796D' }: IconProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
      <Path d={HEART_D} />
    </Svg>
  );
}

export function GoogleIcon({ size = 20 }: { size?: number }) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24">
      <Path d="M21.6 12.2c0-.6-.1-1.3-.2-1.9H12v3.7h5.4a4.6 4.6 0 0 1-2 3v2.5h3.2c1.9-1.7 3-4.3 3-7.3Z" fill="#4285F4" />
      <Path d="M12 22c2.7 0 5-.9 6.6-2.5l-3.2-2.5c-.9.6-2 1-3.4 1-2.6 0-4.8-1.7-5.6-4.1H3.1v2.6A10 10 0 0 0 12 22Z" fill="#34A853" />
      <Path d="M6.4 13.9a6 6 0 0 1 0-3.8V7.5H3.1a10 10 0 0 0 0 9Z" fill="#FBBC05" />
      <Path d="M12 6.1c1.5 0 2.8.5 3.8 1.5l2.8-2.8A10 10 0 0 0 3.1 7.5l3.3 2.6C7.2 7.8 9.4 6.1 12 6.1Z" fill="#EA4335" />
    </Svg>
  );
}

export function AppleIcon({ size = 20, color = '#FFFFFF' }: IconProps) {
  return (
    <Svg width={size} height={size} viewBox="0 0 24 24">
      <Path
        d="M16.4 12.8c0-2.3 1.9-3.4 2-3.5-1.1-1.6-2.8-1.8-3.4-1.8-1.4-.1-2.8.8-3.5.8s-1.8-.8-3-.8c-1.5 0-3 .9-3.8 2.3-1.6 2.8-.4 7 1.2 9.3.8 1.1 1.7 2.4 2.9 2.3 1.2 0 1.6-.7 3-.7s1.8.7 3 .7c1.2 0 2-1.1 2.8-2.2.9-1.3 1.2-2.5 1.3-2.6-.1 0-2.5-1-2.5-3.8ZM14.2 6c.6-.8 1-1.8.9-2.9-.9 0-2 .6-2.6 1.3-.6.7-1.1 1.7-1 2.7 1 .1 2-.4 2.7-1.1Z"
        fill={color}
      />
    </Svg>
  );
}

export function PlayCircleIcon(p: IconProps) {
  return (
    <Stroke {...p}>
      <Circle cx={12} cy={12} r={9.5} />
      <Path d="M10.3 8.7v6.6l5.6-3.3-5.6-3.3Z" fill={p.color ?? '#EFF5F1'} stroke="none" />
    </Stroke>
  );
}

export function DownloadIcon(p: IconProps) {
  return (
    <Stroke {...p}>
      <Path d="M12 3v12" />
      <Path d="m6.5 10.5 5.5 5.5 5.5-5.5" />
      <Path d="M4 20h16" />
    </Stroke>
  );
}
